import * as _ from "lodash";
import * as React from "react";
import { EarthEngine } from "../../earth/earthEngine";
import { FrameTime } from "../../earth/earthEngine";
import { Country } from "../../earth/model/country";
import { Land, LandLevel } from "../../earth/model/land";
import { CountryService } from "../../earth/services/countryService";
import { Anchor } from "../../earth/ui/anchor";
import { Brand } from "../model/brand";
import { KeyedMetrics } from "../model/keyedMetrics";
import { MetricType } from "../model/metricType";
import { MetricsService } from "../services/metricsService";
import { PieChart } from "./pieChart";

const MaxPieChartsToDisplay = 10;
const HorizontalMarginForVisiblePieCharts = 0.2;

interface PieChartManagerProps {
    landLevel: LandLevel;
    selectedMetricType: MetricType;
    selectedBrands: Brand[];
    selectedCountry: Country | null;
}

class PieChartManagerState {
    pieChartDescriptions = new Map<string, PieChartDescription>();
}

class PieChartDescription {
    land: Land;
    metrics: KeyedMetrics;
    ranking: number;
    anchor: Anchor;
    opacity: number;
}

export class PieChartManager extends React.Component<PieChartManagerProps, PieChartManagerState> {
    pieCharts = new Map<string, PieChart>();
    parentFilter: Land | null = null;

    constructor(props: PieChartManagerProps) {
        super(props);
        this.state = new PieChartManagerState();
    }

    update(time: FrameTime) {
        this.setState((prevState, props) => {
            let newState = new PieChartManagerState();

            // Create or update pie-charts for visible lands
            let ranking = 0;
            for (let [land, metrics] of this.getLandsAndMetricsToDisplay()) {
                let key = this.getPieChartKey(land, this.props.selectedMetricType);
                let pieChart = prevState.pieChartDescriptions.get(key);

                if (!pieChart) {
                    let anchor = new Anchor(land.focusPosition);
                    EarthEngine.instance.anchorManager.addAnchor(anchor);
                    pieChart = { land, metrics, ranking, anchor, opacity: 0 };
                } else {
                    pieChart.metrics = metrics;
                    pieChart.ranking = ranking;
                }
                pieChart.opacity = Math.min(pieChart.opacity + time.deltaTime / 0.5, 1.0);

                newState.pieChartDescriptions.set(key, pieChart);
                ranking++;
            }

            // Fade-out pie-charts for lands that are no longer visible and remove them if they are totally faded-out
            for (let [key, prevPieChart] of prevState.pieChartDescriptions) {
                if (!newState.pieChartDescriptions.has(key)) {
                    prevPieChart.opacity = Math.max(prevPieChart.opacity - time.deltaTime / 0.2, 0.0);
                    if (prevPieChart.opacity > 0) {
                        newState.pieChartDescriptions.set(key, prevPieChart);
                    } else {
                        EarthEngine.instance.anchorManager.removeAnchor(prevPieChart.anchor);
                    }
                }
            }

            return newState;
        });

        // Animate and render the created pie-charts
        for (let pieChart of this.pieCharts.values()) {
            pieChart.update(time.deltaTime);
        }
    }

    render(): JSX.Element {
        let pieChartElements: JSX.Element[] = [];
        for (let [key, pieChart] of this.state.pieChartDescriptions) {
            pieChartElements.push(<PieChart key={key}
                                            ref={(ref) => { this.setPieChartFromRef(key, ref); } }
                                            posX={pieChart.anchor.positionOnScreen.x}
                                            posY={pieChart.anchor.positionOnScreen.y}
                                            opacity={pieChart.opacity}
                                            ranking={pieChart.ranking}
                                            metrics={pieChart.metrics}
                                            land={pieChart.land}/>);
        }
        pieChartElements.reverse();

        return <div className="fullscreen">
            { pieChartElements }
        </div>;
    }

    private getLandsAndMetricsToDisplay(): [Land, KeyedMetrics][] {
        let camera = EarthEngine.instance.camera;
        let landLevel = this.props.landLevel;
        let brands = this.props.selectedBrands;
        let metricType = this.props.selectedMetricType;
        let lands = this.props.selectedCountry ?
                        [this.props.selectedCountry] :
                        CountryService.instance.getLandsVisibleByCamera(landLevel, camera, HorizontalMarginForVisiblePieCharts);

        if (this.parentFilter != null) {
            lands = lands.filter(it => it.parent == this.parentFilter);
        }

        let metrics = lands.map(it => MetricsService.instance.getMetricsForLand(metricType, it).filterWithKeys(brands));
        let metricValues = metrics.map(it => it.total);
        let indices = _.sortBy(_.filter([...metricValues.keys()], it => metricValues[it] > 0), it => -metricValues[it]);
        return _.take(indices, MaxPieChartsToDisplay).map<[Land, KeyedMetrics]>(it => [lands[it], metrics[it].flatten()]);
    }

    private getPieChartKey(land: Land, metricType: MetricType): string {
        return `${land.isoCode}:${metricType}`;
    }

    private setPieChartFromRef(key: string, ref: PieChart | null) {
        if (ref) {
            this.pieCharts.set(key, ref);
        } else {
            this.pieCharts.delete(key);
        }
    }

    getPieChartAtCoord(posX: number, posY: number): PieChart | null {
        for (let pieChart of this.pieCharts.values()) {
            if (pieChart.collide(posX, posY)) {
                return pieChart;
            }
        }
        return null;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--1!./src/app/ui/pieChartManager.tsx